/**
 * CopyGrading — Correction automatique de copies par IA (Carbon Design System)
 *
 * Suit exactement le même pattern que ExerciseGenerator / SummaryGenerator.
 */

import { useState } from 'react'
import { TaskComplete } from '@carbon/icons-react'
import { NumberInput } from '@carbon/react'
import {
  FileUpload,
  LanguageSelector,
  AIProcessingCard,
  AiGeneratorPageLayout,
  AiGeneratorFormCard,
  AiFormStep,
  AiGenerateButton,
  GradeResultDisplay,
} from '@sphere/learning/ui'
import { useGradeCopy } from '@sphere/learning/data-access'
import type { SupportedLanguage } from '@sphere/ai/domain'
import { handleAIError } from '@sphere/ai/data-access'
import styles from './CopyGrading.module.scss'

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export default function CopyGrading() {
  const [answerKey, setAnswerKey] = useState<File | null>(null)
  const [studentCopy, setStudentCopy] = useState<File | null>(null)
  const [totalPoints, setTotalPoints] = useState<number>(20)
  const [language, setLanguage] = useState<SupportedLanguage>('fr' as SupportedLanguage)

  const gradeCopyMutation = useGradeCopy()

  const canSubmit =
    !!answerKey &&
    !!studentCopy &&
    totalPoints >= 1 &&
    !gradeCopyMutation.isPending

  const handleGrade = async () => {
    if (!answerKey || !studentCopy) return
    gradeCopyMutation.mutate({
      answerKey,
      studentCopy,
      totalPoints,
      lang: language,
    })
  }

  const handleReset = () => {
    setAnswerKey(null)
    setStudentCopy(null)
    setTotalPoints(20)
    gradeCopyMutation.reset()
  }

  return (
    <AiGeneratorPageLayout
      icon={TaskComplete}
      title="Correction de Copie par IA"
      description="Uploadez un corrigé type et la copie de l'élève. L'IA compare les deux documents et génère une note détaillée avec des commentaires pédagogiques par question."
    >
      {/* ── Formulaire ── */}
      {!gradeCopyMutation.data && (
        <AiGeneratorFormCard title="Corriger une copie" icon={TaskComplete}>
          {/* Step 1 — Corrigé type (pleine largeur) */}
          <AiFormStep step={1} label="Corrigé type (réponses attendues)">
            <FileUpload
              file={answerKey}
              onFileChange={setAnswerKey}
              disabled={gradeCopyMutation.isPending}
            />
          </AiFormStep>

          {/* Step 2 — Copie de l'élève (côte à côte avec step 3) */}
          <AiFormStep step={2} label="Copie de l'élève (à noter)">
            <FileUpload
              file={studentCopy}
              onFileChange={setStudentCopy}
              disabled={gradeCopyMutation.isPending}
            />
          </AiFormStep>

          {/* Step 3 — Barème (côte à côte avec step 2) */}
          <AiFormStep step={3} label="Barème (note sur combien ?)">
            <div className={styles.bareme}>
              <NumberInput
                id="total-points"
                label="Note sur"
                min={1}
                max={200}
                value={totalPoints}
                onChange={(_e: unknown, state: { value: number | string; direction: string }) => {
                  const n = typeof state.value === 'string'
                    ? parseInt(state.value, 10)
                    : state.value
                  if (!isNaN(n) && n >= 1 && n <= 200) setTotalPoints(n)
                }}
                disabled={gradeCopyMutation.isPending}
                helperText="Entre 1 et 200 points"
                hideSteppers={false}
              />
            </div>
          </AiFormStep>

          {/* Step 4 — Langue */}
          <AiFormStep step={4} label="Langue de la correction">
            <LanguageSelector
              value={language}
              onChange={setLanguage}
              disabled={gradeCopyMutation.isPending}
            />
          </AiFormStep>

          {/* Step 5 — Lancer */}
          <AiFormStep step={5} label="Lancer la correction">
            <AiGenerateButton
              onClick={handleGrade}
              disabled={!canSubmit}
              isLoading={gradeCopyMutation.isPending}
              label="Corriger la copie"
              loadingLabel="Correction en cours..."
              icon={TaskComplete}
            />
          </AiFormStep>
        </AiGeneratorFormCard>
      )}

      {/* ── État : chargement ── */}
      {gradeCopyMutation.isPending && (
        <div className={styles.stateCard}>
          <AIProcessingCard
            isLoading
            error={null}
            title="Correction en cours..."
            description="L'IA analyse les deux documents et compare les réponses. Cela peut prendre quelques instants."
          />
        </div>
      )}

      {/* ── État : erreur ── */}
      {gradeCopyMutation.isError && (
        <div className={styles.stateCard}>
          <AIProcessingCard
            isLoading={false}
            error={handleAIError(gradeCopyMutation.error)}
            title="Erreur de correction"
            onRetry={handleGrade}
          />
        </div>
      )}

      {/* ── État : résultat ── */}
      {gradeCopyMutation.data && (
        <div className={styles.resultSection}>
          <GradeResultDisplay result={gradeCopyMutation.data} />

          <div className={styles.resultActions}>
            <button
              type="button"
              className={styles.resetBtn}
              onClick={handleReset}
            >
              ↩ Corriger une autre copie
            </button>
          </div>
        </div>
      )}
    </AiGeneratorPageLayout>
  )
}
